import styled, { css } from 'styled-components'

import {
  IconButton,
  PauseButton,
  ProgressiveAssetVideo,
  ResponsiveImageWithSkeletonWrapper,
  fromLg,
  fromMd,
  fromXl,
  timingDefault,
  untilLg,
  untilMd,
  zIndexForeground,
} from '@syconium/little-miss-figgy'

import { swiperCSS } from '../../../lib/shared-styles'
import { IconBadge as BaseIconBadge, IconSize } from '../IconBadge'
import { iconBadgeStyles } from '../IconBadge/styles'

import {
  thumbnailSwiperWrapperPaddingRight,
  thumbnailSwiperWrapperWidth,
} from './ThumbNailsDisplay/styles'

// Gallery sizing constants — update these if the constrained layout changes.
const galleryMaxHeightPx = 920
const galleryAspectRatio = 0.8

const arrowInset = '20px'

export const Arrow = styled(IconButton).attrs(props => {
  return {
    variant: props.variant ?? 'secondary',
    size: props.size ?? 'medium',
  }
})`
  opacity: 0;
  position: absolute;
  top: 50%;
  transition: opacity ${timingDefault};
`

export const NavigationArrowsWrapper = styled.div`
  position: absolute;
  inset: 0px;
`

export const LeftArrow = styled(Arrow)`
  position: absolute;
  inset-inline-start: ${arrowInset};
  transform: translateY(-50%);
  z-index: 3;
  &:focus {
    opacity: 1;
  }

  .rtl & {
    transform: scaleX(-1);
  }
`

export const RightArrow = styled(Arrow)`
  position: absolute;
  inset-inline-end: ${arrowInset};
  transform: translateY(-50%);
  z-index: 3;
  &:focus {
    opacity: 1;
  }

  .rtl & {
    transform: scaleX(-1);
  }
`

export const HideIfShortOnSpace = styled.div`
  display: contents;
  ${untilMd} {
    display: none;
  }
`

export const RootWrapper = styled.div<{ $constrainHeight?: boolean }>`
  ${swiperCSS}

  position: relative;
  display: flex;
  ${fromMd} {
    position: sticky;
    top: 60px;
  }
  ${o =>
    o.$constrainHeight &&
    css`
      ${fromXl} {
        /* Calculating the total width of the elements for the constrained height, centered layout */
        width: calc(
          ${galleryMaxHeightPx}px * ${galleryAspectRatio} + ${thumbnailSwiperWrapperWidth}px +
            ${thumbnailSwiperWrapperPaddingRight}px
        );
      }
    `}

  .swiper {
    height: 100%;
    ${fromMd} {
      height: fit-content;
    }

    &:hover ${Arrow} {
      opacity: 1;
    }
  }

  .swiper-wrapper {
    min-height: 0px;
    height: auto;
    max-height: 100%;
    ${untilMd} {
      height: 100%;
    }
  }

  .swiper-slide {
    max-height: 100%;
    width: 100%;
    max-width: 100%;
    position: relative;
  }

  .swiper-pagination {
    bottom: 24px;
    display: flex;
    justify-content: right;
    right: 16px;
    width: 95% !important;

    .swiper-pagination-bullet {
      background-color: ${o => o.theme.color.deprecated.black};
      height: 4px;
      margin-left: 4px;
      width: 4px;
    }
    .swiper-pagination-bullet-active {
      background-color: ${o => o.theme.color.deprecated.obsidian};
    }
    .swiper-pagination-video-bullet {
      border-radius: 0;
      background: none;
      width: 0;
      height: 0;
      border-top: 5px solid transparent;
      border-bottom: 5px solid transparent;
      border-left: 8px solid currentColor;
    }
  }
`

export const GallerySliderWrapper = styled.div<{ $constrainHeight?: boolean }>`
  position: relative;
  flex-grow: 1;
  flex-shrink: 1;
  min-height: 0px;
  height: fit-content;
  max-height: 100%;
  min-width: 0px;

  ${untilMd} {
    width: 100%;
    min-height: 400px;
    min-height: min(100dvw, 50lvh);
  }
  ${o =>
    o.$constrainHeight &&
    css`
      ${fromXl} {
        max-height: ${galleryMaxHeightPx}px;
        flex: 0 0 calc(${galleryMaxHeightPx}px * ${galleryAspectRatio});
      }
    `}
`

export const GalleryImagePlayButton = styled(PauseButton)`
  position: absolute;
  top: ${o => o.theme.spacing(4)};
  right: ${o => o.theme.spacing(17.5)};
  ${fromMd} {
    top: ${o => o.theme.spacing(5)};
    right: ${o => o.theme.spacing(5)};
  }
  z-index: ${zIndexForeground};
  box-shadow: none;
`

export const GalleryImage = styled(ResponsiveImageWithSkeletonWrapper)`
  img {
    cursor: zoom-in;
    object-fit: cover;
    object-position: top;

    width: 100%;
    max-width: 100%;

    ${untilMd} {
      width: 100%;
    }
  }
`

export const GalleryImageWrapper = styled.div`
  position: relative;
`

export const GalleryVideoWrapper = styled.div<{ $constrainHeight: boolean }>`
  ${o =>
    o.$constrainHeight &&
    css`
      ${fromXl} {
        width: 100%;
        aspect-ratio: ${galleryAspectRatio};
        max-height: ${galleryMaxHeightPx}px;
      }
    `}
`

export const GalleryVideo = styled(ProgressiveAssetVideo)`
  cursor: zoom-in;

  width: 100%;
  max-width: 100%;

  ${untilMd} {
    width: 100%;
    min-height: 100%;
  }
`

export const IconBadge = styled(BaseIconBadge)<{
  iconBadgePlacement: string
  size: IconSize
  shouldDisplay?: boolean
}>`
  ${o => (iconBadgeStyles[o.iconBadgePlacement] ?? iconBadgeStyles['top left'])?.[o.size]}
  margin: 8px 0;
  position: absolute;
  z-index: ${zIndexForeground};

  ${fromMd} {
    inset-inline-start: 164px;
    margin: 8px 8px;
  }

  ${untilMd} {
    left: -3px;
    margin: 8px 8px;
    top: 0;
  }

  opacity: ${o => (o.shouldDisplay ? 1 : 0)};
  transition: opacity 0.2s ease-in;
`

export const IconBadgeLarge = styled(IconBadge)`
  ${fromLg} {
    display: none;
  }
`

export const IconBadgeExtraLarge = styled(IconBadge)`
  ${untilLg} {
    display: none;
  }
`

export const PDPAudioVideo = styled(ProgressiveAssetVideo)`
  width: 100%;
  max-width: 100%;

  ${untilMd} {
    width: 100%;
    min-height: 100%;
  }
`
